import { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { FaShare, FaVial } from 'react-icons/fa'

export default function DeliveryRoute({ orders }) {
  const [locationPermission, setLocationPermission] = useState('pending')
  const [userLocation, setUserLocation] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [deliveryOrders, setDeliveryOrders] = useState([])
  const [routeUrl, setRouteUrl] = useState('')
  const [routeInfo, setRouteInfo] = useState(null)
  const [optimizing, setOptimizing] = useState(false)
  const [optimizedDisplayOrders, setOptimizedDisplayOrders] = useState(null)
  const mapRef = useRef(null)
  
  // Test mode state
  const [testModeActive, setTestModeActive] = useState(false)
  const [testOrders, setTestOrders] = useState([])
  const [loadingTestData, setLoadingTestData] = useState(false)

  useEffect(() => {
    // Filter orders with status "Betalt"
    const paidOrders = orders.filter(order => order?.data?.status === 'Betalt')
    setDeliveryOrders(paidOrders)

    // Request geolocation permission
    if (navigator.geolocation) {
      setIsLoading(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          })
          setLocationPermission('granted')
          setIsLoading(false)
        },
        (error) => {
          console.error('Error getting location:', error)
          setLocationPermission('denied')
          setIsLoading(false)
          toast.error('Kunne ikke hente din posisjon. Vennligst gi tilgang til posisjon for å bruke kjøreruten.')
        }
      )
    } else {
      setLocationPermission('unsupported')
      setIsLoading(false)
      toast.error('Posisjonering støttes ikke av din nettleser.')
    }
  }, [orders])

  useEffect(() => {
    setOptimizedDisplayOrders(null)
    setRouteInfo(null)
    if (userLocation && (testModeActive ? testOrders.length > 0 : deliveryOrders.length > 0)) {
      generateGoogleMapsRoute()
    }
  }, [userLocation, deliveryOrders, testOrders, testModeActive])
  
  // Function to load test data
  const loadTestData = async () => {
    try {
      setLoadingTestData(true)
      const response = await fetch('/api/dev/test-orders')
      
      if (!response.ok) {
        throw new Error('Failed to fetch test data')
      }
      
      const data = await response.json()
      setTestOrders(data.orders)
      setTestModeActive(true)
      toast.success('Test data loaded successfully!')
    } catch (error) {
      console.error('Error loading test data:', error)
      toast.error('Failed to load test data. Try again.')
    } finally {
      setLoadingTestData(false)
    }
  }
  
  // Function to exit test mode
  const exitTestMode = () => {
    setTestModeActive(false)
    toast.info('Exited test mode')
  }

  const generateGoogleMapsRoute = async () => {
    if (!userLocation) return

    const origin = `${userLocation.lat},${userLocation.lng}`
    const ordersToUse = testModeActive ? testOrders : deliveryOrders

    const addresses = ordersToUse
      .filter(order => order?.customerdata?.address?.formatted)
      .map(order => order.customerdata.address.formatted)

    if (addresses.length === 0) return

    // Try to optimize route via backend
    let optimizedAddresses = addresses
    try {
      setOptimizing(true)
      const res = await fetch('/api/admin/routes/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin, addresses })
      })
      if (res.ok) {
        const data = await res.json()
        optimizedAddresses = data.optimizedAddresses
        setRouteInfo({ duration: data.totalDuration, distance: data.totalDistance })
        // Reorder the display orders to match optimized route
        const ordersWithAddress = ordersToUse.filter(o => o?.customerdata?.address?.formatted)
        setOptimizedDisplayOrders(data.optimizedOrder.map(i => ordersWithAddress[i]))
      } else {
        console.warn('Route optimization failed, using original order')
        toast.warning && toast.warning('Kunne ikke optimalisere ruten, bruker original rekkefølge')
      }
    } catch (err) {
      console.warn('Route optimization error:', err)
    } finally {
      setOptimizing(false)
    }

    // Build Google Maps URL with optimized address order
    const waypoints = optimizedAddresses
      .map(a => encodeURIComponent(a))
      .join('|')

    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${origin}&waypoints=${waypoints}&travelmode=driving`
    setRouteUrl(url)

    if (mapRef.current) {
      const apiKey = window.ENV?.REACT_APP_GOOGLE_MAPS_API_KEY || import.meta.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''
      mapRef.current.src = `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${origin}&destination=${origin}&waypoints=${waypoints}&mode=driving`
    }
  }

  const handleShareRoute = async () => {
    if (!routeUrl) {
      toast.error('Ingen rute å dele')
      return
    }

    // Check if Web Share API is supported
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Bøllefrø Leveringsrute',
          text: 'Her er kjøreruten for leveringer',
          url: routeUrl
        })
        toast.success('Rute delt!')
      } catch (error) {
        console.error('Error sharing route:', error)
        // Fallback to copying to clipboard
        copyToClipboard()
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard()
    }
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(routeUrl)
      .then(() => toast.success('Rutelink kopiert til utklippstavlen!'))
      .catch(err => {
        console.error('Failed to copy:', err)
        toast.error('Kunne ikke kopiere link. Prøv å åpne linken direkte.')
      })
  }

  const openGoogleMaps = () => {
    if (routeUrl) {
      window.open(routeUrl, '_blank')
    }
  }

  if (isLoading) {
    return (
      <RouteSection>
        <p>Laster lokasjon...</p>
      </RouteSection>
    )
  }

  if (locationPermission === 'denied') {
    return (
      <RouteSection>
        <PermissionWarning>
          <h3>Posisjonstilgang nektet</h3>
          <p>For å bruke kjøreruten må du gi tilgang til din posisjon. Vennligst oppdater tillatelser i nettleseren og oppdater siden.</p>
        </PermissionWarning>
      </RouteSection>
    )
  }

  if (locationPermission === 'unsupported') {
    return (
      <RouteSection>
        <PermissionWarning>
          <h3>Posisjonering ikke støttet</h3>
          <p>Din nettleser støtter ikke posisjonering. Prøv en annen nettleser eller enhet.</p>
        </PermissionWarning>
      </RouteSection>
    )
  }

  // Add test mode UI
  const renderTestModeControls = () => {
    if (process.env.NODE_ENV !== 'development') {
      return null
    }

    return (
      <TestModeContainer>
        {!testModeActive ? (
          <TestModeButton 
            onClick={loadTestData} 
            disabled={loadingTestData}
          >
            <FaVial /> {loadingTestData ? 'Laster testdata...' : 'Last testdata'}
          </TestModeButton>
        ) : (
          <TestModeActiveContainer>
            <TestModeActiveIndicator>
              <FaVial /> Testmodus aktiv
            </TestModeActiveIndicator>
            <TestModeButton onClick={exitTestMode}>
              Avslutt testmodus
            </TestModeButton>
          </TestModeActiveContainer>
        )}
      </TestModeContainer>
    )
  }
  
  // If no orders in normal mode, show appropriate message
  if (deliveryOrders.length === 0 && !testModeActive) {
    return (
      <RouteSection>
        <p>Ingen bestillinger med status "Betalt" å levere.</p>
        {renderTestModeControls()}
      </RouteSection>
    )
  }
  
  // If no orders in test mode, only show loading state
  if (testModeActive && testOrders.length === 0 && !loadingTestData) {
    return (
      <RouteSection>
        <p>Ingen testdata tilgjengelig. Prøv å laste på nytt.</p>
        {renderTestModeControls()}
      </RouteSection>
    )
  }

  // Determine which orders to display (prefer optimized order if available)
  const displayOrders = optimizedDisplayOrders || (testModeActive ? testOrders : deliveryOrders)

  return (
    <RouteSection>
      {renderTestModeControls()}
      <RouteControls>
        <h3>
          Kjørerute for {displayOrders.length} leveringer {testModeActive && '(Testdata)'}
          {optimizing && ' — optimaliserer...'}
        </h3>
        {routeInfo && (
          <RouteInfoText>ca. {routeInfo.distance} km / {routeInfo.duration} min</RouteInfoText>
        )}
        <ButtonContainer>
          <Button onClick={openGoogleMaps}>Åpne i Google Maps</Button>
          <ShareButton onClick={handleShareRoute}>
            <FaShare /> Del rute
          </ShareButton>
        </ButtonContainer>
      </RouteControls>
      
      <MapContainer>
        {/* Note: You'll need to replace YOUR_API_KEY with a valid Google Maps API key */}
        <iframe
          ref={mapRef}
          width="100%"
          height="100%"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen
          title="Delivery Route Map"
        ></iframe>
      </MapContainer>

      <DeliveryList>
        <h4>Leveringsliste:</h4>
        {displayOrders.map((order, index) => (
          <DeliveryItem key={order.id}>
            <DeliveryNumber>{index + 1}.</DeliveryNumber>
            <DeliveryInfo>
              <div><strong>{order?.customerdata?.name}</strong></div>
              <div>{order?.customerdata?.address?.formatted}</div>
              <div>{order?.customerdata?.phone_number}</div>
            </DeliveryInfo>
          </DeliveryItem>
        ))}
      </DeliveryList>
    </RouteSection>
  )
}

const RouteSection = styled.div`
  color: white;
  background-color: var(--color-splash-purple);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PermissionWarning = styled.div`
  background-color: rgba(255, 0, 0, 0.2);
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  
  & h3 {
    margin-top: 0;
  }
`

const RouteControls = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  & h3 {
    margin: 0;
  }
`

const RouteInfoText = styled.div`
  font-size: 0.9em;
  opacity: 0.8;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const Button = styled.button`
  background-color: var(--color-splash-yellow);
  border: none;
  color: black;
  padding: 10px 15px;
  font-weight: bold;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`

const ShareButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 5px;
`

const MapContainer = styled.div`
  width: 100%;
  height: 400px;
  background-color: #f0f0f0;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`

const DeliveryList = styled.div`
  background-color: rgba(0, 0, 0, 0.1);
  padding: 15px;
  border-radius: 5px;
  
  & h4 {
    margin-top: 0;
    margin-bottom: 10px;
  }
`

const DeliveryItem = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px dotted rgba(255, 255, 255, 0.3);
  
  &:last-child {
    border-bottom: none;
  }
`

const DeliveryNumber = styled.div`
  font-weight: bold;
  min-width: 25px;
`

const DeliveryInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

// Test mode UI components
const TestModeContainer = styled.div`
  margin-bottom: 15px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.3);
  padding-bottom: 15px;
`

const TestModeButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #4a235a; // Darker purple for test controls
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`

const TestModeActiveContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`

const TestModeActiveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  padding: 6px 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
  color: #f9c74f; // Yellow for the test indicator
`
